import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  EVALUATION_DROP_TOOLTIP,
  PROJECT_ASSESSMENT_STATUSES,
  PROJECT_PEER_EVALUATION_STATUSES,
  NOTIFICATION,
  PROJECT_TYPES
} from '@/constants';
import {
  EmojiRate,
  ProjectAssessmentDTO,
  MemberPeerEvaluationDTO,
  TableHeaderColumns,
  UserDTO,
  UserEngagementDTO
} from '@/models';
import {
  averageByField,
  buildListEmoji,
  buildPeerEvaluation,
  getDecimalParts,
  sumByField,
  isValidNumericInputKey
} from '@/helpers';
import {
  confirmMemberPeerEvaluations,
  fetchMemberPeerEvaluations,
  fetchUserEvaluationDetail,
  getAveragePeerRatingPoint,
  sortArrByUserDTOField,
  sortArrByNumberField
} from '@/services';
import ConfirmationDialog from '@/components/common/confirmation-dialog/ConfirmationDialog.vue';
import EmotionIcon from '@/components/common/emotion-icon/EmotionIcon.vue';
import PopupPeerRating from '@/components/pages/project-evaluation/project-peer-evaluation/pop-up-peer-rating/PopupPeerRating.vue';
import UserInformation from '@/components/common/user-information/UserInformation.vue';
import Tooltip from '@/components/common/tooltip/Tooltip.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';

interface DisplayAverageRatings {
  name: string;
  value: number;
}

interface UserEvaluationDetail {
  peerEvaluations: MemberPeerEvaluationDTO[];
  userEngagement: UserEngagementDTO;
  oneQuarterContributionDrops: number;
  halfContributionDrops: number;
  givingDrops: number;
  receivedDrops: number;
  averageRating: number;
  displayAverageRatings: DisplayAverageRatings[];
}

interface DisplayGivingDrops {
  recipientId: number;
  givingDrops: number;
}

@Component({
  components: {
    ConfirmationDialog,
    EmotionIcon,
    PopupPeerRating,
    UserInformation,
    Tooltip,
    Pagination
  }
})
export default class ProjectPeerEvaluation extends Vue {
  @Prop({ default: false }) canEdit: boolean;
  @Prop() onStepConfirmed: () => void;

  $refs: {
    popupEditRef: HTMLFormElement;
  };

  private peerEvaluations: MemberPeerEvaluationDTO[] = [];
  private currentUser = {} as UserDTO;
  private evaluationConfirmationDialogShown = false;

  private tableHeaderNames = {
    ACCOUNTABILITY: 'Accountability',
    COMMUNICATION: 'Communication',
    SUPPORTIVENESS: 'Supportiveness',
    PERSONAL_ATTRIBUTES: 'Personal Attributes',
    PROFESSIONAL_CONDUCT: 'Professional Conduct',
    GIVING_DROPS: 'Giving Pollens'
  };

  private EVALUATION_DROP_TOOLTIP = EVALUATION_DROP_TOOLTIP;

  private userEvaluationDetail = {
    peerEvaluations: [],
    userEngagement: {
      user: { id: -1 } as UserDTO
    } as UserEngagementDTO,
    oneQuarterContributionDrops: 0,
    halfContributionDrops: 0,
    givingDrops: 0,
    receivedDrops: 0,
    averageRating: 0,
    displayAverageRatings: []
  } as UserEvaluationDetail;

  private emojis = buildListEmoji(true);

  private givingDropsRules = [
    (v: number) => !!v || 'Giving pollens is required',
    (v: number) => (v && this.isGivingDropsValid(v)) || this.getDisplayTooltipGivingDrops()
  ];

  private page = 1;
  private pageCount = 0;
  private itemsPerPage = 10;

  @Watch('userEvaluationDetail.peerEvaluations')
  onUserPeerEvaluationsChanged(): void {
    this.userEvaluationDetail.receivedDrops = sumByField(this.userEvaluationDetail.peerEvaluations, 'givingDrops');
    this.userEvaluationDetail.averageRating = this.userEvaluationDetail.peerEvaluations.reduce(
      (acc, curr) => acc + getAveragePeerRatingPoint(curr),
      0
    );
    this.userEvaluationDetail.displayAverageRatings = this.buildDisplayAverageRatingsFromPeerEvaluations(
      this.userEvaluationDetail.peerEvaluations
    );
    const { contributionDrops } = this.userEvaluationDetail.userEngagement;
    this.userEvaluationDetail.halfContributionDrops = Math.round(contributionDrops / 2);
    this.userEvaluationDetail.oneQuarterContributionDrops = Math.round(contributionDrops / 4);
    this.userEvaluationDetail.givingDrops = this.userEvaluationDetail.oneQuarterContributionDrops;
  }

  buildDisplayAverageRatingsFromPeerEvaluations(peerEvaluations: MemberPeerEvaluationDTO[]): DisplayAverageRatings[] {
    return [
      { name: 'Accountability', value: averageByField(peerEvaluations, 'accountabilityPoint') },
      { name: 'Communication', value: averageByField(peerEvaluations, 'communicationPoint') },
      { name: 'Supportiveness', value: averageByField(peerEvaluations, 'supportivenessPoint') },
      { name: 'Personal Attributes', value: averageByField(peerEvaluations, 'personalAttributesPoint') },
      { name: 'Professional Conduct', value: averageByField(peerEvaluations, 'professionalConductPoint') }
    ];
  }

  //#region computed
  get displayIndividualDrops(): number {
    const { userEngagement } = this.userEvaluationDetail;
    return this.userEngagements.find(ue => ue.user.id === userEngagement.user.id)?.individualDrops ?? 0;
  }

  get projectAssessment(): ProjectAssessmentDTO {
    return this.$store.getters['project/projectAssessment'];
  }

  get userEngagements(): UserEngagementDTO[] {
    return this.$store.getters['project/userEngagements'];
  }

  get members(): MemberPeerEvaluationDTO[] {
    const projectAssessmentId = this.projectAssessment.id;
    const userEngagements = this.projectAssessment.userEngagements || [];
    const peerEvaluations = this.peerEvaluations;
    return userEngagements
      .filter((userEngagement: UserEngagementDTO) => userEngagement.user.id !== this.currentUser.id)
      .map((userEngagement: UserEngagementDTO) => {
        const memberPeerEvaluation =
          peerEvaluations.find(
            (peerEvaluation: MemberPeerEvaluationDTO) => peerEvaluation.recipient.id === userEngagement.user.id
          ) ?? buildPeerEvaluation(this.currentUser, userEngagement.user, projectAssessmentId);
        return {
          ...memberPeerEvaluation,
          givingDrops: this.getDisplayPeerEvaluationGivingDrops(userEngagement.user.id)
        };
      });
  }

  get tableHeaderColumns(): TableHeaderColumns {
    return [
      {
        text: 'Member',
        value: 'recipient'
      },
      {
        text: this.tableHeaderNames.ACCOUNTABILITY,
        value: 'accountabilityPoint',
        class: 'grey--text'
      },
      {
        text: this.tableHeaderNames.COMMUNICATION,
        value: 'communicationPoint',
        class: 'grey--text'
      },
      {
        text: this.tableHeaderNames.SUPPORTIVENESS,
        value: 'supportivenessPoint',
        class: 'grey--text'
      },
      {
        text: this.tableHeaderNames.PERSONAL_ATTRIBUTES,
        value: 'personalAttributesPoint',
        class: 'grey--text'
      },
      {
        text: this.tableHeaderNames.PROFESSIONAL_CONDUCT,
        value: 'professionalConductPoint',
        class: 'grey--text'
      },
      {
        align: 'end',
        text: this.tableHeaderNames.GIVING_DROPS,
        value: 'givingDrops',
        class: 'grey--text'
      },
      {
        align: 'end',
        value: 'actions',
        sortable: false
      }
    ];
  }

  get isConfirmDisabled(): boolean {
    return (
      this.members.filter(member => member.accountabilityPoint === 0).length !== 0 ||
      !this.isGivingDropsValid(this.userEvaluationDetail.givingDrops)
    );
  }

  get isIndividualTrackingProject(): boolean {
    return this.$store.getters['project/projectType'] === PROJECT_TYPES.INDIVIDUAL_TRACKING;
  }

  get hasOneMemberOnly(): boolean {
    return this.userEngagements.length === 1;
  }

  get isPeerEvaluationConfirmed(): boolean {
    return (
      this.userEngagements.find((userEngagement: UserEngagementDTO) => userEngagement.user.id === this.currentUser.id)
        ?.peerEvaluationStatus === PROJECT_PEER_EVALUATION_STATUSES.DONE.label
    );
  }

  get assessmentStatus(): string {
    return this.$store.getters['project/assessmentStatus'];
  }

  get isResultRelease(): boolean {
    return this.assessmentStatus === PROJECT_ASSESSMENT_STATUSES.RESULT_RELEASE;
  }

  get sumAverageRatings(): number {
    return this.peerEvaluations.reduce((acc, curr) => acc + getAveragePeerRatingPoint(curr), 0);
  }

  get listGivingDrops(): DisplayGivingDrops[] {
    let remainingGivingDrops =
      this.userEvaluationDetail.givingDrops -
      this.peerEvaluations.reduce((acc, curr) => acc + Math.floor(this.getPeerEvaluationGivingDrops(curr)), 0);

    return this.peerEvaluations
      .sort(
        (curr, next) =>
          getDecimalParts(this.getPeerEvaluationGivingDrops(next)) -
          getDecimalParts(this.getPeerEvaluationGivingDrops(curr))
      )
      .map(pe => ({
        recipientId: pe.recipient.id,
        givingDrops: Math.floor(this.getPeerEvaluationGivingDrops(pe)) + (remainingGivingDrops-- > 0 ? 1 : 0)
      }));
  }

  //#endregion

  //#region custom columns
  getDisplayIconFromPoint(point: number): EmojiRate | undefined {
    return this.emojis.find(emoji => emoji.point === point);
  }

  getDisplayIconTextFromPoint(point: number): string | undefined {
    return this.emojis.find(emoji => emoji.point === point)?.icon;
  }

  getDisplayPeerEvaluationGivingDrops(recipientId: number): number {
    return this.listGivingDrops.find(item => item.recipientId === recipientId)?.givingDrops || 0;
  }

  //#endregion

  customSort(items: MemberPeerEvaluationDTO[], fields: string[], isDescArr: boolean[]): MemberPeerEvaluationDTO[] {
    const field = fields[0];
    if (field) {
      const isDesc = isDescArr[0];
      switch (field) {
        case 'recipient': {
          return sortArrByUserDTOField(items, field, isDesc);
        }
        case 'accountabilityPoint':
        case 'communicationPoint':
        case 'supportivenessPoint':
        case 'personalAttributesPoint':
        case 'professionalConductPoint':
        case 'givingDrops': {
          return sortArrByNumberField(items, field, isDesc);
        }
        default: {
          return items;
        }
      }
    }
    return items;
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!isValidNumericInputKey(event)) {
      event.preventDefault();
    }
  }

  isGivingDropsValid(givingDrops: number): boolean {
    const { halfContributionDrops, oneQuarterContributionDrops } = this.userEvaluationDetail;
    return givingDrops >= oneQuarterContributionDrops && givingDrops <= halfContributionDrops;
  }

  getDisplayTooltipGivingDrops(): string {
    const { halfContributionDrops, oneQuarterContributionDrops } = this.userEvaluationDetail;
    return ` (min: ${oneQuarterContributionDrops}, max: ${halfContributionDrops})`;
  }

  getPeerEvaluationGivingDrops(peerEvaluation: MemberPeerEvaluationDTO): number {
    return (getAveragePeerRatingPoint(peerEvaluation) / this.sumAverageRatings) * this.userEvaluationDetail.givingDrops;
  }

  async getUserEvaluationDetail(): Promise<void> {
    const { data } = await fetchUserEvaluationDetail(this.projectAssessment.id, this.currentUser.id);
    const { userEngagement, peerEvaluations } = data;
    if (!this.isIndividualTrackingProject) {
      this.userEvaluationDetail.peerEvaluations = peerEvaluations;
    }
    this.userEvaluationDetail.userEngagement = userEngagement;
  }

  getCurrentUserGivingDrops(currentUserId: number, userEngagements: UserEngagementDTO[]): number {
    return userEngagements.find(userEngagement => userEngagement.user.id === currentUserId)?.givingDrops || 0;
  }

  openPopup(peerEvaluation: MemberPeerEvaluationDTO): void {
    if (this.isPeerEvaluationConfirmed) {
      this.$refs.popupEditRef.openMenu(peerEvaluation, this.isPeerEvaluationConfirmed);
    } else {
      this.$refs.popupEditRef.openMenu(peerEvaluation, false);
    }
  }

  async onConfirm(): Promise<void> {
    const projectAssessmentId = this.projectAssessment.id;
    const finalPeerEvaluations = this.peerEvaluations.map(pe => ({
      ...pe,
      givingDrops: this.getDisplayPeerEvaluationGivingDrops(pe.recipient.id)
    }));

    try {
      const { data } = await confirmMemberPeerEvaluations(
        projectAssessmentId,
        this.currentUser.id,
        this.userEvaluationDetail.givingDrops,
        finalPeerEvaluations
      );
      const { userEngagement } = data;
      this.$store.commit('project/updateUserEngagement', { projectAssessmentId, userEngagement });
      this.evaluationConfirmationDialogShown = false;
      const dataNotification = {
        showing: true,
        type: NOTIFICATION.TYPE.success,
        title: NOTIFICATION.TEXT.confirmPeerEvaluationSuccess,
        content: [],
        icon: NOTIFICATION.ICON.success,
        multiline: false,
        height: 0
      };
      this.$store.commit('notification/setNotification', dataNotification);
      this.$emit('onStepConfirmed');
    } catch (e) {
      this.evaluationConfirmationDialogShown = false;

      const data = {
        showing: true,
        type: NOTIFICATION.TYPE.error,
        title: NOTIFICATION.TEXT.confirmPeerEvaluationFailed,
        content: [],
        icon: NOTIFICATION.ICON.error,
        multiline: false,
        height: 0
      };
      this.$store.commit('notification/setNotification', data);
    }
  }

  updatePeerEvaluations(updatedPeerEvaluation: MemberPeerEvaluationDTO): void {
    const existed = this.peerEvaluations.some(pe => pe.id === updatedPeerEvaluation.id);
    if (existed) {
      this.peerEvaluations = this.peerEvaluations.map(pe =>
        pe.id === updatedPeerEvaluation.id ? updatedPeerEvaluation : pe
      );
    } else {
      this.peerEvaluations.push(updatedPeerEvaluation);
    }
  }

  async mounted(): Promise<void> {
    this.userEvaluationDetail.givingDrops = this.getCurrentUserGivingDrops(this.currentUser.id, this.userEngagements);
    await this.getUserEvaluationDetail();
  }

  async created(): Promise<void> {
    this.currentUser = this.$store.getters['app/currentUser'];
    const { data } = await fetchMemberPeerEvaluations(this.projectAssessment.id, this.currentUser.id);
    this.peerEvaluations = data || [];
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
  }
}
